import React, { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Tooltip,
  CircularProgress,
  Alert,
  Checkbox,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Event as EventIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { appointmentAPI } from '../../api/appointments';
import useClinicScopeRefresh from '../../hooks/useClinicScopeRefresh';
import { getApiErrorMessage } from '../../utils/errorUtils';

// Import axios for API calls
import axios from 'axios';

// Get API URL from environment variables
import { API_URL } from '../../utils/apiConfig';
import { formatDateLocalizedSync, formatDateTimeLocalizedSync } from '../../utils/dateTimeUtils';
import { getOrganizationPhoneSettings, formatPhoneForDisplay } from '../../utils/phoneUtils';
import { formatFailedActionMessage, formatSuccessActionMessage } from '../../utils/uiMessages';

// Appointment types for filtering
const APPOINTMENT_TYPES = [
  { value: 'all', label: 'All Types' },
  { value: 'checkup', label: 'Checkup' },
  { value: 'cleaning', label: 'Cleaning' },
  { value: 'consultation', label: 'Consultation' },
  { value: 'follow-up', label: 'Follow-up' },
  { value: 'treatment', label: 'Treatment' },
  { value: 'emergency', label: 'Emergency' },
];

// Appointment statuses
const APPOINTMENT_STATUSES = [
  { value: 'all', label: 'All Statuses' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'no-show', label: 'No Show' },
];

// Note: The following code was removed as it was causing a reference error
// and the statuses are already included in the APPOINTMENT_STATUSES array
// APPOINTMENT_STATUSES.push(
//   { value: 'cancelled', label: 'Cancelled' },
//   { value: 'no-show', label: 'No Show' }
// );


const AppointmentList = () => {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingAppointment, setDeletingAppointment] = useState(false);
  const [creatingRecoveryTaskId, setCreatingRecoveryTaskId] = useState('');
  const [selectedNoShowIds, setSelectedNoShowIds] = useState([]);
  const [phoneCode, setPhoneCode] = useState('91');

  const fetchAppointments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const phoneSettings = await getOrganizationPhoneSettings(true);
      setPhoneCode(phoneSettings.phoneCode || '91');

      const queryParams = new URLSearchParams();
      if (searchTerm) queryParams.append('search', searchTerm);
      if (filterType !== 'all') queryParams.append('type', filterType);
      if (filterStatus !== 'all') queryParams.append('status', filterStatus);
      queryParams.append('page', page + 1);
      queryParams.append('limit', rowsPerPage);

      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/appointments?${queryParams.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      const formattedAppointments = response.data.data.map(appointment => ({
        id: appointment._id,
        patientId: appointment.patient?._id || '',
        patientName: appointment.patient?.name || 'Unknown Patient',
        patientPhone: appointment.patient?.phone || '',
        doctorId: appointment.dentist?._id || '',
        doctorName: appointment.dentist ? `${appointment.dentist.firstName} ${appointment.dentist.lastName}` : 'Unknown Doctor',
        clinicId: appointment.clinic?._id || '',
        clinicName: appointment.clinic?.name || 'Unknown Clinic',
        date: appointment.appointmentDate ? new Date(appointment.appointmentDate).toISOString().split('T')[0] : '',
        startTime: appointment.appointmentDate ? new Date(appointment.appointmentDate).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }) : '',
        endTime: appointment.endTime ? new Date(appointment.endTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }) : '',
        status: appointment.status || 'scheduled',
        type: appointment.appointmentType || 'checkup',
        notes: appointment.notes || '',
        createdAt: appointment.createdAt || new Date().toISOString()
      }));
      
      setAppointments(formattedAppointments);
      setSelectedNoShowIds((prev) => prev.filter((id) => formattedAppointments.some((a) => a.id === id && a.status === 'no-show')));
    } catch (err) {
      console.error('Error fetching appointments:', err);
      setError(formatFailedActionMessage('load', 'appointments'));
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterType, page, rowsPerPage, searchTerm]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);
  useClinicScopeRefresh(fetchAppointments);

  // Handle delete dialog close
  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
  };

  // Handle delete appointment
  const handleDeleteAppointment = async () => {
    try {
      setDeletingAppointment(true);

      // Get token from localStorage
      const token = localStorage.getItem('token');
      
      // Delete appointment via API
      await axios.delete(`${API_URL}/appointments/${selectedAppointment.id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      // Update local state
      setAppointments(appointments.filter(appointment => appointment.id !== selectedAppointment.id));
      toast.success(formatSuccessActionMessage('deleted', 'Appointment'));
    } catch (err) {
      console.error('Error deleting appointment:', err);
      toast.error(getApiErrorMessage(err, formatFailedActionMessage('delete', 'appointment')));
    } finally {
      setDeletingAppointment(false);
      handleDeleteDialogClose();
    }
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleCreateRecoveryTask = async (appointmentId) => {
    try {
      setCreatingRecoveryTaskId(appointmentId);
      const response = await appointmentAPI.createNoShowRecoveryTask(appointmentId);
      if (response?.created) {
        toast.success('Recovery task created');
      } else {
        toast.info('Recovery task already exists');
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err, formatFailedActionMessage('create', 'recovery task')));
    } finally {
      setCreatingRecoveryTaskId('');
    }
  };

  const handleBulkCreateRecoveryTasks = async () => {
    if (selectedNoShowIds.length === 0) {
      toast.info('Select at least one no-show appointment');
      return;
    }
    try {
      const response = await appointmentAPI.bulkCreateNoShowRecoveryTasks(selectedNoShowIds);
      toast.success(`Created: ${response?.created || 0}, already existed: ${response?.alreadyExists || 0}`);
      setSelectedNoShowIds([]);
    } catch (err) {
      toast.error(getApiErrorMessage(err, formatFailedActionMessage('create', 'recovery tasks')));
    }
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle filter type change
  const handleFilterTypeChange = (event) => {
    setFilterType(event.target.value);
    setPage(0);
  };

  // Handle filter status change
  const handleFilterStatusChange = (event) => {
    setFilterStatus(event.target.value);
    setPage(0);
  };

  // Filter appointments based on search term and filters
  const filteredAppointments = appointments.filter(appointment => {
    const searchString = searchTerm.toLowerCase();
    const matchesSearch = (
      appointment.patientName.toLowerCase().includes(searchString) ||
      appointment.doctorName.toLowerCase().includes(searchString) ||
      appointment.patientPhone.includes(searchString) ||
      appointment.id.toLowerCase().includes(searchString)
    );

    const matchesType = filterType === 'all' || appointment.type === filterType;
    const matchesStatus = filterStatus === 'all' || appointment.status === filterStatus;

    return matchesSearch && matchesType && matchesStatus;
  });

  const visibleNoShowIds = filteredAppointments
    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    .filter((appointment) => appointment.status === 'no-show')
    .map((appointment) => appointment.id);
  const allVisibleNoShowSelected =
    visibleNoShowIds.length > 0 && visibleNoShowIds.every((id) => selectedNoShowIds.includes(id));
  const someVisibleNoShowSelected =
    visibleNoShowIds.some((id) => selectedNoShowIds.includes(id)) && !allVisibleNoShowSelected;

  const toggleSelectAllVisibleNoShow = () => {
    setSelectedNoShowIds((prev) => {
      if (allVisibleNoShowSelected) {
        return prev.filter((id) => !visibleNoShowIds.includes(id));
      }
      return [...new Set([...prev, ...visibleNoShowIds])];
    });
  };

  const toggleNoShowSelection = (appointmentId) => {
    setSelectedNoShowIds((prev) =>
      prev.includes(appointmentId) ? prev.filter((id) => id !== appointmentId) : [...prev, appointmentId]
    );
  };

  // Format date & time using organization settings
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return formatDateLocalizedSync(dateString);
  };

  const formatTime = (timeStringOrDate) => {
    if (!timeStringOrDate) return 'N/A';
    // If we receive an ISO/date string, use full datetime formatting and slice time part,
    // otherwise fallback to existing HH:MM(+AM/PM) string.
    if (timeStringOrDate.includes('T') || timeStringOrDate.includes('-')) {
      const localized = formatDateTimeLocalizedSync(timeStringOrDate);
      // Extract time part (after first comma or last space heuristic)
      const parts = localized.split(',');
      return (parts[1] || parts[0] || '').trim();
    }

    // Legacy fallback for plain "HH:MM" strings
    const [hours, minutes] = timeStringOrDate.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Get status chip color
  const getStatusChipColor = (status) => {
    switch (status) {
      case 'scheduled':
        return 'primary';
      case 'confirmed':
        return 'info';
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'error';
      case 'no-show':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'scheduled':
        return <EventIcon fontSize="small" />;
      case 'confirmed':
        return <CheckCircleIcon fontSize="small" />;
      case 'completed':
        return <CheckCircleIcon fontSize="small" />;
      case 'cancelled':
        return <CancelIcon fontSize="small" />;
      case 'no-show':
        return <AccessTimeIcon fontSize="small" />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Appointments
          </Typography>
          <Box>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              component={RouterLink}
              to="/appointments/add"
              sx={{ mr: 2 }}
            >
              New Appointment
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<EventIcon />}
              component={RouterLink}
              to="/calendar"
              sx={{ mr: 2 }}
            >
              Calendar View
            </Button>
            <Button
              variant="contained"
              color="warning"
              startIcon={<AccessTimeIcon />}
              onClick={handleBulkCreateRecoveryTasks}
              disabled={selectedNoShowIds.length === 0}
            >
              Create Recovery Tasks ({selectedNoShowIds.length})
            </Button>
          </Box>
        </Box>

        <Paper elevation={3} sx={{ borderRadius: 2 }}>
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <TextField
              placeholder="Search appointments..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ width: { xs: '100%', sm: '40%' } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel id="appointment-type-label">Type</InputLabel>
                <Select
                  labelId="appointment-type-label"
                  id="appointment-type"
                  value={filterType}
                  label="Type"
                  onChange={handleFilterTypeChange}
                >
                  {APPOINTMENT_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel id="appointment-status-label">Status</InputLabel>
                <Select
                  labelId="appointment-status-label"
                  id="appointment-status"
                  value={filterStatus}
                  label="Status"
                  onChange={handleFilterStatusChange}
                >
                  {APPOINTMENT_STATUSES.map((status) => (
                    <MenuItem key={status.value} value={status.value}>
                      {status.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      indeterminate={someVisibleNoShowSelected}
                      checked={allVisibleNoShowSelected}
                      onChange={toggleSelectAllVisibleNoShow}
                    />
                  </TableCell>
                  <TableCell>ID</TableCell>
                  <TableCell>Patient</TableCell>
                  <TableCell>Doctor</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Time</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Clinic</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAppointments
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((appointment) => (
                    <TableRow key={appointment.id} hover>
                      <TableCell padding="checkbox">
                        {appointment.status === 'no-show' ? (
                          <Checkbox
                            size="small"
                            checked={selectedNoShowIds.includes(appointment.id)}
                            onChange={() => toggleNoShowSelection(appointment.id)}
                          />
                        ) : null}
                      </TableCell>
                      <TableCell>{appointment.id}</TableCell>
                      <TableCell>
                        <Box sx={{ fontWeight: 'medium' }}>
                          {appointment.patientName}
                        </Box>
                        <Box sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                          {formatPhoneForDisplay(appointment.patientPhone, phoneCode)}
                        </Box>
                      </TableCell>
                      <TableCell>{appointment.doctorName}</TableCell>
                      <TableCell>{formatDate(appointment.date)}</TableCell>
                      <TableCell>
                        {formatTime(appointment.startTime)} - {formatTime(appointment.endTime)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={appointment.type.charAt(0).toUpperCase() + appointment.type.slice(1)}
                          size="small"
                          color="default"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getStatusIcon(appointment.status)}
                          label={appointment.status.charAt(0).toUpperCase() + appointment.status.slice(1)}
                          size="small"
                          color={getStatusChipColor(appointment.status)}
                          variant="filled"
                        />
                      </TableCell>
                      <TableCell>{appointment.clinicName}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="View">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/appointments/${appointment.id}`)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/appointments/${appointment.id}/edit`)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setSelectedAppointment(appointment);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {appointment.status === 'no-show' && (
                          <Tooltip title="Create no-show recovery task">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => handleCreateRecoveryTask(appointment.id)}
                              disabled={creatingRecoveryTaskId === appointment.id}
                            >
                              <AccessTimeIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                {filteredAppointments.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      <Typography variant="body1" sx={{ py: 2 }}>
                        No appointments found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filteredAppointments.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </Paper>
      </Box>



      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
      >
        <DialogTitle>Delete Appointment</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the appointment for {selectedAppointment?.patientName} on {selectedAppointment ? formatDate(selectedAppointment.date) : ''} at {selectedAppointment ? formatTime(selectedAppointment.startTime) : ''}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose} disabled={deletingAppointment}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAppointment}
            color="error"
            disabled={deletingAppointment}
            startIcon={deletingAppointment && <CircularProgress size={20} />}
          >
            {deletingAppointment ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AppointmentList;